import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import logo from '../assets/WhippetLogo.png';
import headerHoverImage from '../assets/DSC02696a.JPG';
import styles from '../styles/LandingPage.module.css';

const MOBILE_BREAKPOINT = 640;

const Header = ({ active, toggle, tabletHeroMenuOpen }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const isHeaderActive = active === 'mobileHeader';

  useEffect(() => {
    const mobileQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const desktopQuery = window.matchMedia(
      '(min-width: 1024px) and (hover: hover) and (pointer: fine)'
    );

    const updateViewport = () => {
      const mobile = mobileQuery.matches;
      const desktop = desktopQuery.matches;

      setIsMobile(mobile);
      setIsTablet(!mobile && !desktop);
    };

    updateViewport();

    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', updateViewport);
      desktopQuery.addEventListener('change', updateViewport);
      window.addEventListener('resize', updateViewport);
    } else {
      mobileQuery.addListener(updateViewport);
      desktopQuery.addListener(updateViewport);
      window.addEventListener('resize', updateViewport);
    }

    return () => {
      if (mobileQuery.removeEventListener) {
        mobileQuery.removeEventListener('change', updateViewport);
        desktopQuery.removeEventListener('change', updateViewport);
        window.removeEventListener('resize', updateViewport);
      } else {
        mobileQuery.removeListener(updateViewport);
        desktopQuery.removeListener(updateViewport);
        window.removeEventListener('resize', updateViewport);
      }
    };
  }, []);

  useEffect(() => {
    if (!(isMobile || isTablet) && isHeaderActive) {
      toggle('mobileHeader');
      setMobileMenuOpen(false);
    }
  }, [isMobile, isTablet, isHeaderActive, toggle]);

  useEffect(() => {
    if (!isHeaderActive) {
      setMobileMenuOpen(false);
    }
  }, [isHeaderActive]);

  const handleHeaderClick = () => {
    if (!(isMobile || isTablet)) return;
    toggle('mobileHeader');
  };

  const handleKeyDown = (e) => {
    if (!(isMobile || isTablet)) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleHeaderClick();
    }
  };

  return (
    <motion.div
      className={`${styles.panel} ${styles.header} ${isHeaderActive ? styles.panelActive : ''}`}
      initial={{ opacity: 0, y: -14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      onClick={handleHeaderClick}
      role={isMobile || isTablet ? 'button' : undefined}
      tabIndex={isMobile || isTablet ? 0 : undefined}
      onKeyDown={handleKeyDown}
      aria-expanded={isMobile || isTablet ? isHeaderActive : undefined}
      aria-label={isMobile || isTablet ? 'Toggle restaurant panel' : undefined}
    >
      <img
        src={headerHoverImage}
        alt=""
        aria-hidden="true"
        className={styles.headerBg}
      />

      <AnimatePresence mode="wait">
        {(!(isMobile || isTablet) || !isHeaderActive) ? (
          <>
            <motion.div
              key="header-default"
              className={styles.headerInner}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <img
                src={logo}
                alt="Whippet and Pickle logo"
                className={styles.logo}
              />

              <p className={styles.headerTitle}>
                The Whippet and Pickle
              </p>
            </motion.div>

            {!(isMobile || isTablet) && (
              <div className={styles.headerHoverContent}>
                <p>
                  The Whippet and Pickle is a calm, seasonal space shaped by
                  Scandinavian simplicity, gently brewed coffee, and relaxed fine dining —
                  offering thoughtful food and warm hospitality.
                </p>
              </div>
            )}
          </>
        ) : isTablet ? (
          <motion.div
            key="header-tablet-content"
            className={styles.headerTabletContent}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <button
              type="button"
              className={styles.headerTabletParagraphButton}
              onClick={(e) => {
                e.stopPropagation();
                toggle('mobileHeader');
              }}
              aria-label="Close restaurant panel"
            >
              The Whippet and Pickle is a calm, seasonal space shaped by
              Scandinavian simplicity, gently brewed coffee, and relaxed fine dining —
              offering thoughtful food and warm hospitality.
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="header-mobile-content"
            className={styles.mobileHeaderContent}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <button
              type="button"
              className={styles.mobileHeaderHeadingButton}
              onClick={(e) => {
                e.stopPropagation();
                toggle('mobileHeader');
                setMobileMenuOpen(false);
              }}
              aria-label={isHeaderActive ? 'Close restaurant panel' : 'Open restaurant panel'}
            >
              {isHeaderActive && !tabletHeroMenuOpen && (
                <span className={styles.mobileHeaderCloseIcon}>×</span>
              )}
              <span className={styles.mobileHeaderTitleText}>
                A local neighbourhood restaurant.
              </span>
              <span className={styles.mobileHeaderTitleTextParagraph}>
                The Whippet and Pickle is a calm, seasonal space shaped by
                Scandinavian simplicity, gently brewed coffee, and relaxed fine dining —
                offering thoughtful food and warm hospitality.
              </span>
            </button>

            <div className={styles.mobileHeaderActions}>
              <motion.a
                href="https://booking.favouritetable.com/?sitecode=2052"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.bookingButton}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                onClick={(e) => e.stopPropagation()}
              >
                Book Here
              </motion.a>

              <button
                type="button"
                className={styles.mobileMenuTrigger}
                onClick={(e) => {
                  e.stopPropagation();
                  setMobileMenuOpen(true);
                }}
              >
                Sample menu
              </button>

              <AnimatePresence>
                {mobileMenuOpen && (
                  <motion.div
                    className={styles.mobileMenuContent}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 4 }}
                    transition={{ duration: 0.2 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ul
                      onClick={(e) => {
                        e.stopPropagation();
                        setMobileMenuOpen(false);
                      }}
                    >
                      <li>Bread — cultured butter</li>
                      <li>Prawn — fennel, citrus</li>
                      <li>Celeriac — hazelnut, brown butter</li>
                      <li>Salmon — dill, cucumber</li>
                      <li>Beef — shallot, greens, jus</li>
                      <li>Orange — vanilla cream</li>
                      <li>Chocolate — geranium ice cream</li>
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Header;